import { AxiosError } from 'axios'
import { DataTableController } from 'components/common'
import ActiveFilterChip from 'components/common/activeFilterChip'
import { useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import toast from 'react-hot-toast'
import { downloadOpenOrders } from 'utils/api/orderApi'
import { filterEmptyValues } from 'utils/helpers'
import { usePageAccess, useQuery } from 'hooks'
import {
  FILTER_FIELD_OPEN_ORDERS,
  openOrderColumns,
  OpenOrderFiltersForm,
  OpenOrdersTable,
} from '.'
import { fetchPosOpenOrderCounts } from 'utils/api/openOrderApi'

// ─── types — keys match EXACTLY what the API returns ─────────────────────────
type StatusCounts = {
  'On hold': number
  'Picking in progress': number
  'Pending for pickup': number
}

// ─── card config — keys match EXACTLY what the API returns ───────────────────
const STATUS_CONFIG = [
  {
    key: 'total' as const,
    label: 'Total Open Orders',
    accent: 'border-l-gray-700',
    iconBg: 'bg-gray-100',
    iconColor: 'text-gray-600',
    icon: (
      <svg
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
        />
      </svg>
    ),
  },
  {
    key: 'On hold' as const,
    label: 'On Hold',
    accent: 'border-l-yellow-400',
    iconBg: 'bg-yellow-50',
    iconColor: 'text-yellow-500',
    icon: (
      <svg
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    key: 'Picking in progress' as const,
    label: 'Picking in Progress',
    accent: 'border-l-blue-500',
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-500',
    icon: (
      <svg
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13 10V3L4 14h7v7l9-11h-7z"
        />
      </svg>
    ),
  },
  {
    key: 'Pending for pickup' as const,
    label: 'Pending for Pickup',
    accent: 'border-l-orange-400',
    iconBg: 'bg-orange-50',
    iconColor: 'text-orange-500',
    icon: (
      <svg
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
]

// ─── count card ───────────────────────────────────────────────────────────────
const CountCard = ({
  label,
  count,
  accent,
  icon,
  iconBg,
  iconColor,
  loading,
}: {
  label: string
  count: number
  accent: string
  icon: React.ReactNode
  iconBg: string
  iconColor: string
  loading: boolean
}) => (
  <div
    className={`flex items-center gap-4 rounded-xl border border-l-4 border-gray-100 bg-white px-5 py-4 shadow-sm ${accent}`}
  >
    <div
      className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${iconBg}`}
    >
      <span className={iconColor}>{icon}</span>
    </div>
    <div className="min-w-0 flex-1">
      <p className="truncate text-xs font-semibold uppercase tracking-wider text-gray-400">
        {label}
      </p>
      {loading ? (
        <div className="mt-1.5 h-6 w-10 animate-pulse rounded bg-gray-100" />
      ) : (
        <p className="text-2xl font-bold text-gray-800">{count}</p>
      )}
    </div>
  </div>
)

// ─── page ─────────────────────────────────────────────────────────────────────
const OpenOrderListPage = () => {
  const { isEditor } = usePageAccess()
  const [showFilter, setShowFilter] = useState(false)
  const [selectedColumns, setSelectedColumns] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [countsLoading, setCountsLoading] = useState<boolean>(true)
  const [statusCounts, setStatusCounts] = useState<StatusCounts>({
    'On hold': 0,
    'Picking in progress': 0,
    'Pending for pickup': 0,
  })
  const [total, setTotal] = useState<number>(0)

  const { getQuery } = useQuery()

  const loadCounts = useCallback(async () => {
    setCountsLoading(true)
    try {
      const { data } = await fetchPosOpenOrderCounts()
      setStatusCounts(data.statusCounts)
      setTotal(data.total)
    } catch {
      // non-critical — silently fail, cards stay at 0
    } finally {
      setCountsLoading(false)
    }
  }, [])

  // Fetch counts once on mount
  useEffect(() => {
    loadCounts()
  }, [loadCounts])

  const toggleFilterForm = () => setShowFilter(!showFilter)

  const handleExport = async () => {
    const filters = getQuery(FILTER_FIELD_OPEN_ORDERS)

    if (!filters) {
      return toast.error('Filters are required to export data!', {
        duration: 3000,
      })
    }

    const masterFilter = filterEmptyValues(filters)

    if (Object.keys(masterFilter).length <= 0) {
      return toast.error('Please select at least one filter to export data!', {
        duration: 3000,
      })
    }

    const loadingToast = toast.loading('Loading...', { duration: Infinity })

    try {
      await downloadOpenOrders({ filters: masterFilter })
      toast.success('File will be sent to you in email!', {
        id: loadingToast,
        duration: 3000,
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data?.errors?.msg?.runningService) {
          return toast.error(error.response.data.errors.msg.runningService, {
            id: loadingToast,
            duration: 3000,
          })
        }
      }
      toast.error('Failed to export Open Orders data.', {
        id: loadingToast,
        duration: 3000,
      })
    }
  }

  return (
    <>
      <Helmet>
        <title>GAG Aggregator | Open Orders</title>
      </Helmet>

      <div className="mb-3 flex flex-wrap items-center justify-between md:mb-4 md:flex-nowrap">
        <h3 className="title">Open Orders</h3>

        {/* Refresh counts button */}
        <button
          type="button"
          onClick={loadCounts}
          disabled={countsLoading}
          title="Refresh counts"
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 shadow-sm transition hover:bg-gray-50 disabled:opacity-50"
        >
          <svg
            className={`h-4 w-4 ${countsLoading ? 'animate-spin' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Refresh Counts
        </button>
      </div>

      {/* ── Status Count Cards ── */}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        {STATUS_CONFIG.map(
          ({ key, label, accent, icon, iconBg, iconColor }) => (
            <CountCard
              key={key}
              label={label}
              count={key === 'total' ? total : statusCounts[key]}
              accent={accent}
              icon={icon}
              iconBg={iconBg}
              iconColor={iconColor}
              loading={countsLoading}
            />
          ),
        )}
      </div>

      {/* ── Table Controls ── */}
      <DataTableController
        isEditor={isEditor}
        columns={openOrderColumns}
        handleFilter={toggleFilterForm}
        showFilter={showFilter}
        handleExport={handleExport}
        activeColumnKey="openOrder"
        placeholder="Search by Order No, and Short Code"
        defaultSelectedColumns={selectedColumns}
        loading={loading}
      />

      {!showFilter && <ActiveFilterChip filterKey={FILTER_FIELD_OPEN_ORDERS} />}

      {showFilter ? (
        <OpenOrderFiltersForm toggleFilterForm={toggleFilterForm} />
      ) : (
        <OpenOrdersTable
          setSelectedColumns={setSelectedColumns}
          loading={loading}
          setLoading={setLoading}
        />
      )}
    </>
  )
}

export default OpenOrderListPage
